// type-only import — erased at runtime. The actual mqtt.js module is
// loaded dynamically inside the connect effect so consumers that mount
// chat conditionally (the storefront help widget) never ship the MQTT
// stack in their entry bundle; the CRM pays the same async chunk once
// at provider mount.
import type { MqttClient } from 'mqtt';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	ChatConnection,
	ChatConnectionConfig,
	ChatConnectionFailureReason,
	ChatConnectionState,
} from './chat-connection-types';
import {
	isTerminalConnectError,
	MQTT_BASE_RECONNECT_MS,
	nextReconnectMs,
} from './mqtt-backoff';

/**
 * Stop reconnecting after this many consecutive errors with no
 * successful connect in between. Combined with the exponential
 * backoff in `nextReconnectMs` (caps at 30s), this gives the broker
 * roughly 2 + 4 + 8 + 16 + 30 + 30 + 30 + 30 = ~150s to come back
 * before we give up and surface a clear failure to the user.
 *
 * The point of the cap is twofold:
 *   1. Stop hammering broker / authorizer Lambda when something is
 *      genuinely broken (misconfigured URL, bad token, etc). Without
 *      it, a single misconfigured environment causes hundreds of
 *      authorizer invocations per minute per browser tab.
 *   2. Give the UI a clear "this isn't going to fix itself" signal so
 *      we can render a real error state with a manual retry button
 *      instead of the perpetual "Reconnecting…" banner that lies
 *      about progress being made.
 */
const MAX_CONSECUTIVE_CONNECT_FAILURES = 8;

/**
 * React hook that manages a single MQTT connection for the lifetime of
 * the component that mounts it. Typical usage is at the top of a chat
 * provider so the connection is shared across the whole UI.
 *
 * Behaviour:
 *   - Lazily connects on mount.
 *   - Auto-generates a client id of the form `{principalId}-{deviceId}`
 *     where deviceId persists across reloads (stored in localStorage) so
 *     a browser tab reclaims its slot after refresh instead of becoming
 *     a stranger the broker doesn't know.
 *   - Reconnects automatically on transient failures. Token refresh is
 *     handled by passing a new `token` to the hook — the connection
 *     tears down and re-establishes. On a terminal auth rejection it
 *     also self-heals once via the `refreshToken` callback before
 *     surfacing `failed` — the MQTT mirror of the HTTP 401 interceptor.
 *   - When the component unmounts, the client is ended gracefully.
 */
export function useChatConnection(
	config: ChatConnectionConfig | null,
): ChatConnection {
	const [state, setState] = useState<ChatConnectionState>('idle');
	const [failureReason, setFailureReason] =
		useState<ChatConnectionFailureReason | null>(null);
	// Bumped by `retry()` to force the connection effect to re-run and
	// build a fresh client after we've ended one in `failed`. Without
	// a tracked dep, React's effect dependency comparison wouldn't
	// notice that anything changed (wsUrl/principalId/deviceId all
	// stay the same when the user retries the same session).
	const [retryNonce, setRetryNonce] = useState(0);
	const clientRef = useRef<MqttClient | null>(null);
	const handlersRef = useRef(
		new Map<string, Set<(topic: string, payload: unknown) => void>>(),
	);
	// Latest auth token, read at CONNECT-time only. We deliberately do
	// NOT rebuild the mqtt client when the token rotates — the auth
	// service refreshes tokens on a steady cadence and rebuilding the
	// client every time would manifest as a constant reconnect loop in
	// the broker logs ("Client X already connected, closing old
	// connection" repeated every couple of seconds). Instead, we update
	// `client.options.password` so the next reconnect (if any) uses the
	// fresh token.
	const tokenRef = useRef<string | undefined>(config?.token);

	// Latest force-refresh callback, mirrored into a ref so the connect
	// effect's terminal-error handler always invokes the current one
	// without needing `config` in its dependency array.
	const refreshTokenRef = useRef(config?.refreshToken);
	refreshTokenRef.current = config?.refreshToken;

	// Reactive-refresh attempts since the last successful connect. Caps
	// recovery at one refresh+reconnect per connection — the same
	// single-retry contract the HTTP 401 interceptor uses. Without the
	// cap, each forced refresh mints a new token, so the
	// `freshToken !== tokenRef.current` guard alone would let a
	// genuinely-rejected session loop refresh→reconnect forever.
	const authRefreshCountRef = useRef(0);

	// Per-TAB clientId suffix. Two tabs of the CRM open simultaneously
	// would otherwise share a single mqtt clientId and fight each
	// other forever — every tab's connect kicks the other off, the
	// other's reconnect timer fires 2s later and kicks back. Symptom
	// in the broker logs: "Client X already connected, closing old
	// connection" repeated every couple of seconds.
	//
	// sessionStorage is the right scope: per tab, persists across
	// reloads of THAT tab only, so refresh-in-place still reclaims
	// the same session instead of accumulating zombies — but a second
	// tab gets a different suffix and a different clientId.
	const deviceId = useMemo(() => {
		if (config?.deviceId != null) return config.deviceId;
		if (typeof window === 'undefined') return fallbackId();
		const key = 'chat:tabId';
		let id = window.sessionStorage.getItem(key);
		if (id == null || id === '') {
			id = fallbackId();
			window.sessionStorage.setItem(key, id);
		}
		return id;
	}, [config?.deviceId]);

	const wsUrl = config?.wsUrl ?? null;
	const principalId = config?.principalId ?? null;
	// Read at connect-time only (like the token). Derived from the same
	// identity as principalId, so it changes in lockstep — the connect
	// effect re-runs on principalId change and picks up the new value.
	const presenceTopic = config?.presenceTopic ?? null;

	// Token rotation: keep the ref + the live mqtt client's password
	// option in sync so the NEXT reconnect uses the fresh token, but
	// don't tear down the current healthy connection.
	useEffect(() => {
		tokenRef.current = config?.token;
		if (clientRef.current != null && config?.token != null) {
			clientRef.current.options.password = config.token;
		}
	}, [config?.token]);

	useEffect(() => {
		if (wsUrl == null || principalId == null) {
			setState('idle');
			return;
		}

		const clientId = `${principalId}-${deviceId}`;
		// Track consecutive errors WITHOUT an intervening successful
		// connect. A single successful connect resets the counter, so
		// a long-lived session can drop and recover indefinitely
		// without ever hitting the cap; the cap only fires when we
		// truly can't get in.
		let consecutiveErrors = 0;
		// Have we ever hit `connect` on this client? Used to decide
		// whether a `close` after errors should land in
		// `reconnecting` (had a session, lost it) or stay in
		// `connecting` (never made it).
		let everConnected = false;
		// Set true when we proactively `client.end(true)` because of
		// a terminal failure or hitting the cap. Prevents the
		// downstream `close` event from clobbering the `failed` state
		// back to `reconnecting` on its way out.
		let givingUp = false;
		// Set true by cleanup so async work still in flight (the mqtt
		// dynamic import, the reactive token refresh below) knows the
		// effect was torn down and a newer one — or none — now owns the
		// connection.
		let disposed = false;
		// Owned by the async body below; cleanup ends it if it exists.
		let client: MqttClient | null = null;

		setState('connecting');
		setFailureReason(null);

		void (async () => {
			// Dynamic import keeps mqtt.js out of consumers' entry bundles
			// (see the module-header note). The interop dance mirrors what
			// the portal widget shipped with: mqtt publishes CJS, so the
			// namespace may or may not carry a `default`.
			let mqttModule: typeof import('mqtt');
			try {
				mqttModule = await import('mqtt');
			} catch {
				if (disposed) return;
				setFailureReason('network');
				setState('failed');
				return;
			}
			if (disposed) return;
			const mqtt = mqttModule.default ?? mqttModule;

			// Last Will: on an ungraceful drop (tab close, crash, network
			// loss) the broker publishes this to our presence topic so the
			// server clears presence promptly — the cost cleanup that lets us
			// lean on a slower heartbeat. Graceful disconnects discard the
			// will, so steady-state presence still relies on the heartbeat.
			// (For a portal customer it also abandons a still-waiting
			// support-queue entry so a rep never claims a ghost.)
			const will =
				presenceTopic != null
					? {
							topic: presenceTopic,
							payload: JSON.stringify({ at: Date.now(), offline: true }),
							qos: 1 as const,
							retain: false,
						}
					: undefined;

			// Local const so the handler closures below see a non-null,
			// narrowed reference; the outer `client` exists only for the
			// effect cleanup to end.
			const c = mqtt.connect(wsUrl, {
				clientId,
				username: principalId,
				password: tokenRef.current,
				protocolVersion: 5,
				reconnectPeriod: MQTT_BASE_RECONNECT_MS,
				clean: true,
				keepalive: 30,
				will,
			});
			client = c;

			c.on('connect', () => {
				everConnected = true;
				consecutiveErrors = 0;
				// A healthy connect means the current token is valid —
				// re-arm the one-shot reactive auth refresh for the next
				// time it expires.
				authRefreshCountRef.current = 0;
				setState('connected');
				setFailureReason(null);
				// Reset to base period after a successful connect so a
				// later transient drop starts fresh (no carry-over from
				// previous outage).
				c.options.reconnectPeriod = MQTT_BASE_RECONNECT_MS;
				// Re-subscribe to every filter the app had registered before
				// the reconnect — mqtt.js remembers some of this but MQTT 5
				// with clean=true doesn't, so we manage it ourselves.
				for (const filter of handlersRef.current.keys()) {
					c.subscribe(filter, { qos: 1 });
				}
			});
			// `close` fires both on graceful disconnect AND on every
			// reconnect-cycle drop. Don't downgrade past `failed` (we may
			// have already given up) and don't downgrade `connecting` —
			// only an established session that got dropped should land in
			// `reconnecting`.
			const handleDrop = () => {
				if (givingUp) return;
				setState((s) => {
					if (s === 'failed') return s;
					if (everConnected) return 'reconnecting';
					return 'connecting';
				});
			};
			c.on('close', handleDrop);
			c.on('offline', handleDrop);

			// Reactive auth recovery — the MQTT mirror of the HTTP layer's
			// 401 interceptor (ApiService: 401 → refreshAccessToken() →
			// retry once → then fail). The broker rejected our credentials
			// with a terminal CONNACK code (0x87 Not authorized for an
			// expired/invalid token — from both Mosquitto and AWS IoT Core).
			// Before giving up we force ONE token refresh and reconnect with
			// the fresh token; only if that's also rejected — or there is no
			// refresh callback, or the refresh yields no new token — do we
			// surface a definitive `failed`.
			const handleTerminalConnectError = async (): Promise<void> => {
				// Suppress the close/offline handlers for the duration of
				// the async refresh — their state writes would just churn.
				givingUp = true;
				// Credentials were rejected; stop the client now. Letting
				// mqtt.js auto-reconnect would re-present the same stale
				// password and thrash the authorizer.
				c.end(true);

				const refresh = refreshTokenRef.current;
				if (refresh != null && authRefreshCountRef.current < 1) {
					authRefreshCountRef.current += 1;
					let freshToken: string | null = null;
					try {
						freshToken = await refresh();
					} catch {
						freshToken = null;
					}
					// Cleanup ran while the refresh was in flight — a newer
					// effect (or none) owns the connection now; don't touch
					// state.
					if (disposed) return;
					if (freshToken != null && freshToken !== tokenRef.current) {
						// Recreate the client with the fresh token: the
						// effect reads tokenRef at mqtt.connect()-time and
						// bumping retryNonce re-runs it. Setting tokenRef
						// directly (rather than waiting for the config.token
						// prop to round-trip through React) keeps it
						// deterministic.
						tokenRef.current = freshToken;
						clientRef.current = null;
						setRetryNonce((n) => n + 1);
						return;
					}
				}

				// No refresh callback, the refresh failed, or the freshly
				// refreshed token was still rejected — definitive auth
				// failure. React won't auto-recreate; the user retries via
				// `retry()` or by re-logging in.
				clientRef.current = null;
				setFailureReason('auth');
				setState('failed');
			};

			c.on('error', (err: Error & { code?: number }) => {
				if (isTerminalConnectError(err)) {
					void handleTerminalConnectError();
					return;
				}
				// Non-terminal (network jitter, broker restart, auth
				// Lambda misconfigured so CONNACK is rejected silently,
				// etc). Back off gracefully — mqtt.js reads
				// `client.options.reconnectPeriod` on each retry — and
				// give up after the cap so we don't hammer broker /
				// authorizer Lambda forever in a misconfigured env.
				consecutiveErrors += 1;
				if (
					consecutiveErrors >= MAX_CONSECUTIVE_CONNECT_FAILURES &&
					!everConnected
				) {
					givingUp = true;
					clientRef.current = null;
					setFailureReason('network');
					setState('failed');
					c.end(true);
					return;
				}
				c.options.reconnectPeriod = nextReconnectMs(consecutiveErrors);
			});

			c.on('message', (topic, payload) => {
				let parsed: unknown;
				try {
					parsed = JSON.parse(payload.toString('utf8'));
				} catch {
					parsed = payload;
				}
				// Deliver to every handler whose filter matches this topic.
				for (const [filter, handlers] of handlersRef.current) {
					if (filterMatches(filter, topic)) {
						for (const h of handlers) h(topic, parsed);
					}
				}
			});

			clientRef.current = c;
		})();

		return () => {
			disposed = true;
			clientRef.current = null;
			// May be null when cleanup runs before the dynamic import
			// resolved — the disposed flag stops the async body from
			// building a client afterwards.
			client?.end(false);
			// Don't overwrite `failed` on cleanup — if we got here
			// because the principalId changed (logout), the next
			// effect run will reset state via setState('connecting').
			// If we got here from an unmount mid-failure, holding
			// `failed` is the right read for any consumers still
			// watching.
			setState((s) => (s === 'failed' ? s : 'idle'));
		};
	}, [wsUrl, principalId, deviceId, retryNonce]);

	const retry = useCallback(() => {
		// Only meaningful when we've given up; in any other state
		// the existing effect is already trying or already happy.
		setState((s) => {
			if (s !== 'failed') return s;
			setFailureReason(null);
			// Re-arm the one-shot reactive auth refresh — a manual
			// retry that reconnects with a now-stale token should
			// still be allowed to refresh once.
			authRefreshCountRef.current = 0;
			setRetryNonce((n) => n + 1);
			return 'connecting';
		});
	}, []);

	return useMemo<ChatConnection>(
		() => ({
			get connected() {
				return state === 'connected';
			},
			get state() {
				return state;
			},
			get failureReason() {
				return failureReason;
			},
			get principalId() {
				return principalId;
			},
			retry,
			publish(topic, payload) {
				const client = clientRef.current;
				if (client == null || !client.connected) {
					return Promise.reject(new Error('Chat connection not ready'));
				}
				const body =
					payload instanceof Uint8Array
						? payload
						: typeof payload === 'string'
							? payload
							: JSON.stringify(payload);
				return new Promise<void>((resolve, reject) => {
					// mqtt.js accepts Uint8Array payloads at runtime (its browser
					// build treats them like Buffer), but its typings only admit
					// string | Buffer.
					client.publish(topic, body as string | Buffer, { qos: 1 }, (err) => {
						if (err != null) reject(err);
						else resolve();
					});
				});
			},
			subscribe(filter, handler) {
				let set = handlersRef.current.get(filter);
				if (set == null) {
					set = new Set();
					handlersRef.current.set(filter, set);
					const client = clientRef.current;
					if (client != null && client.connected) {
						client.subscribe(filter, { qos: 1 });
					}
				}
				set.add(handler);
				return () => {
					const s = handlersRef.current.get(filter);
					if (s == null) return;
					s.delete(handler);
					if (s.size === 0) {
						handlersRef.current.delete(filter);
						const client = clientRef.current;
						if (client != null && client.connected) {
							client.unsubscribe(filter);
						}
					}
				};
			},
		}),
		[state, failureReason, principalId, retry],
	);
}

function fallbackId(): string {
	if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
		return crypto.randomUUID();
	}
	return `dev-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * MQTT topic filter match. Supports + (single segment) and # (multi-segment trailing).
 */
function filterMatches(filter: string, topic: string): boolean {
	const f = filter.split('/');
	const t = topic.split('/');
	for (let i = 0; i < f.length; i++) {
		if (f[i] === '#') return true;
		if (t[i] == null) return false;
		if (f[i] === '+') continue;
		if (f[i] !== t[i]) return false;
	}
	return f.length === t.length;
}
